import { createFileRoute } from '@tanstack/react-router'
import { todayIso } from '@/lib/today'
import { useMemo, useState } from 'react'
import { Sparkles, ChevronDown, Home, MapPin } from 'lucide-react'
import { toast } from 'sonner'
import { useSchedule } from '@/hooks/use-schedule'
import { useTeam, useCompany } from '@/hooks/use-company'
import { Page, PageHeader, Card, btnPrimary } from '@/components/rm'
import { RouteMap, type RouteResult, type RouteStop } from '@/components/route-map'
import { googleMapsKey } from '@/lib/google-maps'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/_app/routes')({ component: RoutesPage })

const fmtTime = (iso: string | null) => (iso ? phFormat(new Date(iso), 'time', { hour: 'numeric', minute: '2-digit' }).replace(' ', '').toLowerCase() : '—')

function RoutesPage() {
  const { members } = useTeam()
  const { company } = useCompany()
  const [techId, setTechId] = useState<string>('')
  const { jobs } = useSchedule(todayIso())

  const [reopt, setReopt] = useState(0)
  const [result, setResult] = useState<RouteResult | null>(null)
  const hasKey = !!googleMapsKey()

  const tech = members.find((m) => m.id === techId) ?? members[0]
  const techName = tech ? [tech.first_name, tech.last_name].filter(Boolean).join(' ') || tech.email || 'Tech' : 'Tech'

  const stops = useMemo(() => jobs
    .filter((j) => (tech ? j.user_id === tech.id : true))
    .sort((a, b) => (a.planned_start_date ?? '').localeCompare(b.planned_start_date ?? '')), [jobs, tech])

  const depot = useMemo(() => [company?.address, company?.city, company?.state, (company as { postal_code?: string } | null)?.postal_code]
    .filter(Boolean).join(', ') || null, [company])

  const routeStops: RouteStop[] = useMemo(
    () => stops.filter((s) => s.address?.trim()).map((s) => ({ id: s.id, label: s.name || 'Job', address: s.address! })),
    [stops],
  )
  const missing = stops.length - routeStops.length

  // Order the sidebar by the optimised visiting order once we have a result.
  const orderedStops = useMemo(() => {
    if (!result?.orderedIds?.length) return stops
    const byId = new Map(stops.map((s) => [String(s.id), s]))
    const seq = result.orderedIds.map((id) => byId.get(String(id))).filter(Boolean) as typeof stops
    const rest = stops.filter((s) => !result.orderedIds.some((id) => String(id) === String(s.id)))
    return [...seq, ...rest]
  }, [stops, result])

  return (
    <Page>
      <PageHeader
        eyebrow="Dispatch"
        title="Routes"
        subtitle={`${stops.length} stops today${missing > 0 ? ` · ${missing} without address` : ''}`}
        actions={
          <>
            <div className="flex items-center gap-2 rounded-[10px] border border-token bg-token-surface px-3 py-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-token-selected text-[11px] font-bold text-token-warning">{(techName[0] ?? 'T').toUpperCase()}</span>
              <select value={techId} onChange={(e) => setTechId(e.target.value)} className="bg-transparent text-[13px] font-bold text-token-primary outline-none">
                {members.length === 0 && <option value="">No technicians</option>}
                {members.map((m) => <option key={m.id} value={m.id}>{[m.first_name, m.last_name].filter(Boolean).join(' ') || m.email}</option>)}
              </select>
              <ChevronDown className="h-3.5 w-3.5 text-token-muted" />
            </div>
            <button onClick={() => { if (routeStops.length === 0) { toast.message('No routable stops', { description: 'Add addresses to today’s jobs to compute a route.' }); return } setReopt((n) => n + 1) }} className={btnPrimary}>
              <Sparkles className="h-4 w-4" /> Re-optimise route
            </button>
          </>
        }
      />

      {/* Live totals banner */}
      <div className="mb-[18px] mt-5 flex flex-wrap items-center gap-x-[18px] gap-y-3 rounded-[14px] border border-transparent bg-token-raised px-[22px] py-4 text-token-primary shadow-[var(--surface-shadow)]">
        <div className="flex items-center gap-2"><span className="text-[16px] text-accent">✦</span><span className="text-[13.5px] font-bold">Optimised drive route</span></div>
        <div className="hidden h-[22px] w-px bg-token sm:block" />
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-[12.5px] text-token-secondary">
          <div><span className="font-display text-[18px] font-bold text-token-primary">{result ? result.distanceText : '—'}</span> distance</div>
          <div><span className="font-display text-[18px] font-bold text-token-success">{result ? result.durationText : '—'}</span> drive time</div>
          <div><span className="font-display text-[18px] font-bold text-token-primary">{routeStops.length}</span> stops</div>
        </div>
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-[1.6fr_1fr]">
        {/* Live map */}
        <div className="relative h-[560px] overflow-hidden rounded-[16px] border border-transparent bg-canvas-subtle shadow-[var(--surface-shadow)]">
          {hasKey ? (
            <RouteMap depot={depot} stops={routeStops} reoptimizeKey={reopt} onResult={setResult} />
          ) : (
            <div className="flex h-full items-center justify-center p-6 text-center text-[13px] text-token-secondary">
              Add <code className="mx-1 rounded bg-token-surface px-1.5 py-0.5 text-[12px]">VITE_GOOGLE_MAPS_API_KEY</code> to enable the live route map.
            </div>
          )}
        </div>

        {/* Optimised order */}
        <Card>
          <div className="border-b border-token-subtle px-[18px] py-4">
            <p className="font-display text-[15px] font-bold text-token-primary">Optimised order</p>
          </div>
          {depot && (
            <>
              <div className="flex items-center gap-3 px-[18px] py-3.5 text-[12.5px] text-token-secondary">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand text-brand-fg"><Home className="h-3 w-3" /></span>
                Depot · {depot}
              </div>
              <div className="h-px bg-token-subtle" />
            </>
          )}
          {stops.length === 0 ? (
            <p className="px-[18px] py-10 text-center text-[13px] text-token-secondary">No stops scheduled for {techName} today.</p>
          ) : orderedStops.map((s, i) => {
            const noAddr = !s.address?.trim()
            return (
              <div key={s.id} className="flex gap-3 border-b border-token-subtle px-[18px] py-3.5 last:border-0">
                <span className={`font-display flex h-6 w-6 flex-none items-center justify-center rounded-full text-[12px] font-bold ${noAddr ? 'bg-token-hover text-token-secondary' : 'bg-brand text-brand-fg'}`}>{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13.5px] font-bold text-token-primary">{s.name || 'Job'}</div>
                  <div className="flex items-center gap-1 truncate text-[12px] text-token-secondary">
                    {noAddr && <MapPin className="h-3 w-3 flex-none text-token-warning" />}
                    <span className="truncate">{s.address?.trim() || 'No address'} · {fmtTime(s.planned_start_date)}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </Card>
      </div>
    </Page>
  )
}
